import { useState } from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import { useParams } from 'react-router-dom';
import { userService } from '../../../shared/services/users/usersServices';
import { Button, TextField, Box, Alert, IconButton, InputAdornment } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const validationSchema = Yup.object().shape({
  newPassword: Yup.string().required('Nova senha obrigatória').min(6, 'Mínimo 6 caracteres'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'As senhas não coincidem')
    .required('Confirme a nova senha'),
});

export default function ChangePasswordForm({ userId }: { userId?: string }) {
  const { user } = useAuth();
  const params = useParams();
 
  const id = userId || params.id || user.id;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleTogglePassword = () => setShowPassword((prev) => !prev);
  const handleToggleConfirm = () => setShowConfirm((prev) => !prev);

  return (
    <Formik
      initialValues={{ newPassword: '', confirmPassword: '' }}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        setLoading(true);
        setError('');
        setSuccess('');
        try {
          await userService.changePassword(id, values.newPassword);
          setSuccess('Senha alterada com sucesso!');
          resetForm();
        } catch (err: any) {
          setError(
            err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            'Erro ao alterar senha'
          );
        } finally {
          setLoading(false);
        }
      }}
    >
      {({ errors, touched, handleChange, values }) => (
        <Form>
          <Box sx={{ mt: 2 }}>
            <Field
              as={TextField}
              label="Nova senha"
              name="newPassword"
              type={showPassword ? 'text' : 'password'}
              value={values.newPassword}
              onChange={handleChange}
              required
              fullWidth
              disabled={loading}
              error={!!(touched.newPassword && errors.newPassword)}
              helperText={touched.newPassword && errors.newPassword}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleTogglePassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Field
              as={TextField}
              label="Confirme a nova senha"
              name="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              value={values.confirmPassword}
              onChange={handleChange}
              required
              fullWidth
              disabled={loading}
              error={!!(touched.confirmPassword && errors.confirmPassword)}
              helperText={touched.confirmPassword && errors.confirmPassword}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleToggleConfirm} edge="end">
                      {showConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button type="submit" variant="contained" disabled={loading}>
              Alterar senha
            </Button>
            {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </Box>
        </Form>
      )}
    </Formik>
  );
}